import { ReactElement, useCallback, useLayoutEffect, useMemo } from 'react';
import { getItemsOnPage, getPagesNum } from 'ud-ui/components/category/utils';

import CategoryList from 'ud-ui/components/category/components/list/category-list';
import { CategoryListContextProvider } from 'ud-ui/components/category/hooks/use-category-list-context';
import { CategoryListContextType } from '../hooks/use-category-list-context';
import { CategorySkeleton } from '../components/list/category-skeleton';
import EmptyList from 'ud-ui/components/category/components/list/empty-list';
import ListPageWrapper from 'ud-ui/components/category/pages/list-page-wrapper';
import { UDListItemProps } from 'ud-ui/components/list/components/list-item';
import { useLocalStorage } from 'shared/hooks/use-local-storage';
import { useQueryParams } from 'shared/hooks/use-query-params';

function CategoryListPage(props: IProps) {
  const { getFiltered, getNextPages, onCreateClick, children: items = [], isLoading, categoryName, text } = props;

  const [params, setParams] = useQueryParams({
    page: {
      key: 'page',
      defaultValue: 1,
      parser: Number,
    },
    filter: {
      key: 'filter',
      defaultValue: '',
    },
  });

  const [storage, setStorage] = useLocalStorage({
    perPage: {
      key: `${categoryName}PerPage`,
      defaultValue: 10,
      parser: Number,
    },
  });

  useLayoutEffect(() => {
    getFiltered?.(params.filter);
  }, [getFiltered, params.filter]);

  const totalPages = useMemo(() => {
    const pages = getPagesNum(items.length, storage.perPage);
    if (params.page > pages) setParams({ page: Math.max(pages, 1) });
    return pages;
  }, [items.length, storage.perPage]);

  const onPageChange = useCallback(
    (page: number) => {
      setParams({ page });
      if (totalPages === page) {
        getNextPages?.();
      }
    },
    [getNextPages, setParams, totalPages],
  );

  const onFilterChange = useCallback(
    (value: string) => {
      setParams({ filter: value, page: 1 });
      getFiltered?.(value);
    },
    [getFiltered, setParams],
  );

  const onPerPageChange = useCallback(
    (perPage: number) => {
      setStorage({ perPage });
    },
    [setStorage],
  );

  const contextValue: CategoryListContextType = useMemo(() => {
    return {
      buttonProps: {
        text: text?.addButtonText ?? `Добавить`,
        variant: `dark`,
      },
      textProps: {
        title: params.filter ? 'Ничего не найдено' : `Список пуст`,
        subtitle: params.filter ? `Попробуйте изменить ваш поиск` : '',
      },
      styleProps: {
        containerClassName: 'p-4',
        textClassName: 'my-2',
        buttonClassName: 'mt-3',
      },
      isFilterActive: Boolean(params.filter),
      filter: params.filter,
      onCreateClick: onCreateClick,
    };
  }, [onCreateClick, params.filter]);

  const itemsOnPage = useMemo(() => {
    return getItemsOnPage(items, params.page, storage.perPage);
  }, [items, params.page, storage.perPage]);

  const renderContent = () => {
    if (isLoading) {
      return <CategorySkeleton count={storage.perPage} className="mb-2" />;
    }

    if (itemsOnPage.length === 0) {
      return <EmptyList />;
    }

    return <CategoryList items={itemsOnPage} styleProps={contextValue.styleProps} />;
  };

  return (
    <ListPageWrapper
      pages={totalPages}
      currentPage={params.page}
      onPageChange={onPageChange}
      filterText={params.filter}
      filterInputPlaceholder={text?.searchPlaceholder ?? `Поиск`}
      onFilterChange={onFilterChange}
      perPage={storage.perPage}
      onChangePerPage={onPerPageChange}
    >
      <CategoryListContextProvider value={contextValue}>{renderContent()}</CategoryListContextProvider>
    </ListPageWrapper>
  );
}

export default CategoryListPage;

interface IProps {
  getNextPages?: () => void;
  getFiltered?: (value: string) => void;
  onCreateClick?: () => void;
  children?: ReactElement<UDListItemProps>[];
  isLoading?: boolean;

  categoryName: string;

  text?: {
    searchPlaceholder?: string;
    addButtonText?: string;
  };
}
